import { suggestEcuCode } from "./ecuCode";
import { useState, useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { createSubjectUE } from "../../../api/ue";
import { getAllSubject } from "../../../api/pedagogicElement";
import { getAllTeachers } from "../../../api/teachers";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { getCoursesByAcademicYear } from "../../../api/cours";

//Typage des props parents
type UUID = string;
type Props = {
  show: boolean;
  ueID: UUID;
  codeUE: string;
  classID: UUID;
  subjectUes: any;
  onClose: () => void;
  onSaved?: () => void;
};

interface Teacher {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  sexe: String;
  number: String;
  adresse: String;
  birthday: String;
  statut: boolean;
  createdAt: string;
}

const CreateSubjectUEModal = ({
  show,
  classID,
  onClose,
  onSaved,
  ueID,
  codeUE,
  subjectUes,
}: Props) => {
  //Initialisation de l'année academmique actuelle
  const { selectedYear } = useAcademicYear();
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [mode, setMode] = useState<"course" | "subject">("course");
  const [subjects, setSubjects] = useState<any[]>([]);

  const [allCourses, setAllCourses] = useState<any[]>([]);

  //Schéma de notre formulaire
  const createSchema = z.object({
    credit: z
      .number({ invalid_type_error: "Le credit de l'ECU est requis" })
      .positive("Le crédit doit être supérieur à zéro"),
    codeECU: z.string().nonempty("Le code de l'UCE est requis"),
    coefficient: z
      .number({ invalid_type_error: "Le coefficient de l'UCE est requis" })
      .min(0, "Le coefficient doit être au minimum 0"),
    id_teacher: z.string().nonempty("Le professeur est requis"),
    id_course: z.string().optional(),
    id_subject: z.string().optional(),
  }).superRefine((data, ctx) => {
    const field = mode === "course" ? "id_course" : "id_subject";
    if (!data[field]) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: mode === "course" ? "Le cours est requis" : "La matière est requise" });
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
    defaultValues: { id_course: "", id_subject: "", id_teacher: "" },
  });

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const finalData = {
        ...data,
        id_course: mode === "course" ? data.id_course : null,
        id_subject: mode === "subject" ? data.id_subject : undefined,
        id_ue: ueID ?? "",
      };
      // console.log(finalData);
      //Envoi des datas pour création vers le backend
      await createSubjectUE(finalData);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("ECU ajoutée !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => { if (show) { reset({ id_course: "", id_subject: "", id_teacher: "", codeECU: suggestEcuCode(codeUE, subjectUes || []) }); setMode("course"); } }, [show, ueID, reset, codeUE, subjectUes]);

  //On recupère la liste des période en appelant sa fonction
  useEffect(() => {
    if (!show) return;
    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;

    getAllTeachers(
      buildPermissionContext(Resource.SUBJECT_UE, Action.CREATE),
    ).then((teacher) => {
      // console.log(teacher);
      setTeachers(teacher.data);
    }).catch(() => toast.error("Impossible de charger les enseignants"));

  }, [show, selectedYear?.id]);

  useEffect(() => {
    if (!show) return;
    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;
    fetchCourseByYear();
  }, [show, classID, selectedYear?.id]);

  const fetchCourseByYear = async () => {
    try {
      if (!selectedYear) return;
      const response = await getCoursesByAcademicYear(selectedYear?.id);
      const cours = response.data || [];
      // console.log("All course: ", cours);
      setAllCourses(
        cours.filter(
          (c: any) =>
            c.status === "Approuvé" &&
            c.id_classes?.some((cl: any) => cl === classID) &&
            !c.class_assignments?.find((assignment: any) => assignment.id_class === classID)?.id_ue,
        ),
      );
      // console.log("All courses: ", allCourses);
    } catch (error: any) {
      // console.log("errreur lors de la récupération des cours: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };
  // changer automatiquement de prof en mode course
  useEffect(() => {
    const matchingTeacher = teachers.find(
      (teacher) =>
        teacher.id ===
        allCourses.find((c: any) => c.id === watch("id_course"))?.TeacherProfil
          ?.id,
    );
    // console.log("Changer le cours!:", matchingTeacher);
    if (matchingTeacher) setValue("id_teacher", matchingTeacher.id);
  }, [watch("id_course"), teachers, allCourses]);

  useEffect(() => {
    if (!show || !selectedYear?.id) return;
    let active = true;
    getAllSubject(selectedYear.id, buildPermissionContext(Resource.SUBJECT_UE, Action.CREATE)).then(response => {
      if (active) setSubjects(response.data || []);
    }).catch(() => { if (active) toast.error("Impossible de charger les matières"); });
    return () => { active = false; };
  }, [show, selectedYear?.id]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Création d'un ECU</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row">
              <div className="col-12 mb-3">
                <label className="form-label">Créer l’ECU à partir de</label>
                <select className="form-select" value={mode} onChange={e => { setMode(e.target.value as "course" | "subject"); setValue("id_course", ""); }}>
                  <option value="course">Un cours</option>
                  <option value="subject">Une matière sans cours</option>
                </select>
              </div>
              {mode === "subject" && <div className="col-12 mb-3">
                <label className="form-label">Matière</label>
                <select {...register("id_subject")} className={errors.id_subject ? "form-select is-invalid" : "form-select"}>
                  <option value="">Sélectionner une matière</option>
                  {subjects.map(subject => <option key={subject.id} value={subject.id}>{subject.label}</option>)}
                </select>
                {errors.id_subject && <div className="invalid-feedback">{errors.id_subject.message}</div>}
              </div>}
              {mode === "course" && <>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Cours non rattaché à une UE</label>
                  <select {...register("id_course")} className={errors.id_course ? "form-select is-invalid" : "form-select"}>
                    <option value="">Sélectionner</option>
                    {allCourses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course?.Subject?.label} — {course?.TeacherProfil?.firstname} {course?.TeacherProfil?.lastname}
                      </option>
                    ))}
                  </select>
                  {errors.id_course && <div className="invalid-feedback">{errors.id_course.message}</div>}
                  {!allCourses.length && <div className="form-text">Aucun cours approuvé disponible. Vous pouvez sélectionner une matière sans cours.</div>}
                </div>
              </div>

              </>}

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Professeur</label>
                  <select
                    {...register("id_teacher")}
                    disabled={mode === "course"}
                    aria-label="Default select example"
                    className={
                      errors.id_teacher
                        ? "form-select is-invalid"
                        : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.firstname} {teacher.lastname}
                      </option>
                    ))}
                  </select>
                  {errors.id_teacher && (
                    <div className="invalid-feedback">
                      {errors.id_teacher.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Code ECU
                  </label>
                  <input
                    {...register("codeECU")}
                    type="text"
                    className={
                      errors.codeECU
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Code ECU"
                  />
                  {errors.codeECU && (
                    <div className="invalid-feedback">
                      {errors.codeECU.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Crédit
                  </label>
                  <input
                    {...register("credit", { valueAsNumber: true })}
                    type="number"
                    className={
                      errors.credit ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: UE-MGT"
                  />
                  {errors.credit && (
                    <div className="invalid-feedback">
                      {errors.credit.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Coefficient
                  </label>
                  <input
                    {...register("coefficient", { valueAsNumber: true })}
                    type="number"
                    className={
                      errors.coefficient
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: UE-MGT"
                  />
                  {errors.coefficient && (
                    <div className="invalid-feedback">
                      {errors.coefficient.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              // disabled={isSubmitting||!selectedYear?.statut}
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  CreateSubjectUEModal,
  [buildPermission(Resource.SUBJECT_UE, Action.CREATE)],
  "masquer",
);
